import { FC, useMemo } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';

import BlueIOSSwitch from '../../pages/private/Profile/components/GeneralSettings/BlueIOSSwitch';
import {
  useGetTripReminderQuery,
  useGetTripStopReminderQuery,
  usePutTripReminderMutation,
  usePutTripStopReminderMutation,
  type ReminderConfig,
} from '../../store/services/notificationsApi';

import styles from './EventReminderSettings.module.scss';
import { isReminderOffsetAllowedBeforeEvent, parseTripStartToDate } from './eventReminderTime';

export const OFFSET_ORDER = ['15m', '45m', '1h', '3h', '1d', '1w'] as const;

export const REMINDER_OFFSET_OPTIONS: { value: string; label: string }[] = [
  { value: '15m', label: '15 minutes before' },
  { value: '45m', label: '45 minutes before' },
  { value: '1h', label: '1 hour before' },
  { value: '3h', label: '3 hours before' },
  { value: '1d', label: '1 day before' },
  { value: '1w', label: '1 week before' },
];

const DEFAULT_OFFSETS: string[] = ['15m'];
const SELECT_MENU_Z_INDEX = 12000;

export function getTripReminderStartIso(startDate: string | undefined): string | null {
  if (!startDate?.trim()) return null;
  const d = parseTripStartToDate(startDate);
  const t = d.getTime();
  if (Number.isNaN(t)) return null;
  return d.toISOString();
}

export function getTripReminderEndIso(endDate: string | undefined): string | null {
  if (!endDate?.trim()) return null;
  const s = endDate.trim();
  if (s.includes('T')) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  }
  const parts = s.split('-').map(Number);
  if (parts.length === 3 && parts.every(n => Number.isFinite(n))) {
    const [y, mo, d] = parts;
    return new Date(y, mo - 1, d, 23, 59, 59, 999).toISOString();
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function sortReminderOffsets(offsets: string[]): string[] {
  const unique = Array.from(new Set(offsets));
  const isKnown = (o: string): o is (typeof OFFSET_ORDER)[number] => (OFFSET_ORDER as readonly string[]).includes(o);
  const known = unique.filter(isKnown).sort((a, b) => OFFSET_ORDER.indexOf(a) - OFFSET_ORDER.indexOf(b));
  const unknown = unique.filter(o => !isKnown(o)).sort((a, b) => a.localeCompare(b));
  return [...known, ...unknown];
}

function labelForOffset(value: string): string {
  return REMINDER_OFFSET_OPTIONS.find(o => o.value === value)?.label ?? value;
}

function isEnabled(cfg: ReminderConfig | undefined): boolean {
  if (!cfg) return false;
  return Boolean(cfg.start || (cfg.offsets?.length ?? 0) > 0);
}

type TripProps = {
  scope: 'trip';
  tripId: number;
};

type StopProps = {
  scope: 'stop';
  stopId: number;
};

type EventReminderSettingsProps = (TripProps | StopProps) & {
  title?: string;
  description?: string;
  skip?: boolean;
  compact?: boolean;
  eventStartsAtIso?: string | null;
  eventEndsAtIso?: string | null;
};

function offsetAllowedForWindow(offsetToken: string, msUntilStart: number, noTimeGate: boolean): boolean {
  if (noTimeGate || msUntilStart === Infinity) return true;
  return isReminderOffsetAllowedBeforeEvent(offsetToken, msUntilStart);
}

function firstSelectableOffset(msUntilStart: number, noTimeGate: boolean): string[] {
  if (noTimeGate) return DEFAULT_OFFSETS;
  for (const o of OFFSET_ORDER) {
    if (isReminderOffsetAllowedBeforeEvent(o, msUntilStart)) return [o];
  }
  return [];
}

const EventReminderSettings: FC<EventReminderSettingsProps> = props => {
  const { title, description, skip = false, compact = false, eventStartsAtIso, eventEndsAtIso } = props;

  const tripId = props.scope === 'trip' ? props.tripId : 0;
  const stopId = props.scope === 'stop' ? props.stopId : 0;

  const tripQuery = useGetTripReminderQuery(tripId, {
    skip: skip || props.scope !== 'trip',
  });
  const stopQuery = useGetTripStopReminderQuery(stopId, {
    skip: skip || props.scope !== 'stop',
  });

  const data = props.scope === 'trip' ? tripQuery.data : stopQuery.data;
  const isLoading = props.scope === 'trip' ? tripQuery.isLoading : stopQuery.isLoading;
  const isFetching = props.scope === 'trip' ? tripQuery.isFetching : stopQuery.isFetching;

  const [putTrip, { isLoading: savingTrip }] = usePutTripReminderMutation();
  const [putStop, { isLoading: savingStop }] = usePutTripStopReminderMutation();

  const saving = props.scope === 'trip' ? savingTrip : savingStop;

  const timeGate = useMemo(() => {
    if (eventStartsAtIso == null || eventStartsAtIso === '') {
      return {
        noTimeGate: true as const,
        msUntilStart: Infinity,
        remindersLocked: false,
        lockReason: null as 'past' | 'too_soon' | null,
      };
    }
    const startMs = new Date(eventStartsAtIso).getTime();
    if (Number.isNaN(startMs)) {
      return { noTimeGate: true as const, msUntilStart: Infinity, remindersLocked: false, lockReason: null };
    }
    const msUntilStart = startMs - Date.now();
    if (msUntilStart <= 0) {
      return {
        noTimeGate: false as const,
        msUntilStart,
        remindersLocked: true,
        lockReason: 'past' as const,
      };
    }
    const anyStandard = OFFSET_ORDER.some(o => isReminderOffsetAllowedBeforeEvent(o, msUntilStart));
    if (!anyStandard) {
      return {
        noTimeGate: false as const,
        msUntilStart,
        remindersLocked: true,
        lockReason: 'too_soon' as const,
      };
    }
    return { noTimeGate: false as const, msUntilStart, remindersLocked: false, lockReason: null };
  }, [eventStartsAtIso]);

  const { noTimeGate, msUntilStart, remindersLocked, lockReason } = timeGate;

  const enabled = useMemo(() => isEnabled(data), [data]);

  const rawSelectedOffsets = useMemo(() => sortReminderOffsets(data?.offsets ?? []), [data?.offsets]);

  const selectedOffsets = useMemo(() => {
    if (remindersLocked || noTimeGate || msUntilStart === Infinity) return rawSelectedOffsets;
    return rawSelectedOffsets.filter(o => offsetAllowedForWindow(o, msUntilStart, false));
  }, [rawSelectedOffsets, remindersLocked, noTimeGate, msUntilStart]);

  const eventHasEnded = useMemo(() => {
    if (eventEndsAtIso == null || eventEndsAtIso === '') return false;
    const endMs = new Date(eventEndsAtIso).getTime();
    if (Number.isNaN(endMs)) return false;
    return endMs < Date.now();
  }, [eventEndsAtIso]);

  const selectOptions = useMemo(() => {
    const extras = (data?.offsets ?? []).filter(o => {
      if (REMINDER_OFFSET_OPTIONS.some(x => x.value === o)) return false;
      return offsetAllowedForWindow(o, msUntilStart, noTimeGate);
    });
    const extraOpts = extras.map(value => ({ value, label: value }));
    const standard = REMINDER_OFFSET_OPTIONS.filter(opt => offsetAllowedForWindow(opt.value, msUntilStart, noTimeGate));
    return [...extraOpts, ...standard];
  }, [data?.offsets, msUntilStart, noTimeGate]);

  const persist = async (body: ReminderConfig) => {
    if (props.scope === 'trip') {
      await putTrip({ tripId, body }).unwrap();
    } else {
      await putStop({ stopId, body }).unwrap();
    }
  };

  const handleToggle = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (remindersLocked) return;
    if (!checked) {
      await persist({ start: false, offsets: [] });
      return;
    }
    const existing = data?.offsets?.filter(o => offsetAllowedForWindow(o, msUntilStart, noTimeGate)) ?? [];
    const next = existing.length > 0 ? sortReminderOffsets(existing) : firstSelectableOffset(msUntilStart, noTimeGate);
    if (next.length === 0) return;
    await persist({
      start: true,
      offsets: next,
    });
  };

  const handleOffsetsChange = (e: SelectChangeEvent<string[]>) => {
    if (remindersLocked) return;
    const v = e.target.value;
    const raw = typeof v === 'string' ? v.split(',') : v;
    const next = raw.filter(o => offsetAllowedForWindow(o, msUntilStart, noTimeGate));
    void persist({ start: true, offsets: sortReminderOffsets(next) });
  };

  if (skip) {
    return null;
  }

  const busy = isLoading || isFetching || saving;
  const offsetDisabled = !enabled || busy || remindersLocked;
  const toggleDisabled = busy || remindersLocked;

  const showHeader = !compact && (title || description);

  const lockHint =
    remindersLocked && lockReason === 'past' && !eventHasEnded
      ? props.scope === 'trip'
        ? 'Trip already started.'
        : 'This stop has already started.'
      : remindersLocked && lockReason === 'too_soon'
        ? 'Not enough time before start for a reminder.'
        : null;

  return (
    <Box className={`${styles.block} ${compact ? styles.compact : ''}`}>
      {showHeader ? (
        <>
          {title ? (
            <Typography
              variant="h10Semibold"
              className={styles.title}
            >
              {title}
            </Typography>
          ) : null}
          {description ? (
            <Typography
              variant="h11"
              className={styles.hint}
            >
              {description}
            </Typography>
          ) : null}
        </>
      ) : null}

      <Box className={styles.row}>
        <Box className={styles.labelRow}>
          {busy && !data ? (
            <CircularProgress size={22} />
          ) : (
            <FormControlLabel
              control={
                <BlueIOSSwitch
                  checked={!remindersLocked && enabled}
                  onChange={handleToggle}
                  disabled={toggleDisabled}
                />
              }
              label={
                <Typography
                  variant="h10"
                  sx={{ color: '#222B3A' }}
                >
                  Notifications
                </Typography>
              }
              sx={{ margin: 0, gap: 1 }}
            />
          )}
        </Box>

        <Box className={styles.offsetWrap}>
          <Select
            size="small"
            multiple
            className={styles.select}
            value={selectedOffsets}
            onChange={handleOffsetsChange}
            disabled={offsetDisabled}
            displayEmpty
            renderValue={selected => {
              const arr = selected as string[];
              if (!arr.length) {
                return (
                  <Typography
                    component="span"
                    variant="h10"
                    sx={{ color: '#5e6a81' }}
                    className={styles.selectValue}
                  >
                    Remind before start
                  </Typography>
                );
              }
              return (
                <Typography
                  component="span"
                  variant="h10"
                  sx={{ color: '#222B3A' }}
                  className={styles.selectValue}
                >
                  {arr.map(labelForOffset).join(', ')}
                </Typography>
              );
            }}
            MenuProps={{
              sx: { zIndex: SELECT_MENU_Z_INDEX },
              PaperProps: {
                sx: {
                  maxHeight: 320,
                  zIndex: SELECT_MENU_Z_INDEX,
                },
              },
            }}
          >
            {selectOptions.map(opt => {
              const allowed = offsetAllowedForWindow(opt.value, msUntilStart, noTimeGate);
              return (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  disabled={!allowed}
                  sx={{ opacity: allowed ? 1 : 0.45 }}
                >
                  <Checkbox
                    size="small"
                    checked={selectedOffsets.indexOf(opt.value) > -1}
                    disabled={!allowed}
                    sx={{ mr: 0.5, p: 0.5 }}
                  />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              );
            })}
          </Select>
        </Box>
      </Box>

      {lockHint ? (
        <Typography
          variant="h11"
          sx={{ color: '#5e6a81', mt: 0.5 }}
        >
          {lockHint}
        </Typography>
      ) : null}
    </Box>
  );
};

export default EventReminderSettings;
